// Festival data for Cigar Advisor

export interface Festival {
  id: string;
  name: string;
  shortName: string;
  location: string;
  venue?: string;
  address?: string;
  description: string;
  date?: string;
  time?: string;
  admissionInfo?: string;
  website?: string;
  highlights?: string[];
  mapCenter: { lat: number; lng: number };
  mapZoom: number;
  active: boolean;
}

export interface Vendor {
  id: string;
  name: string;
  festivalId: string;
  boothNumber?: string;
  location?: { lat: number; lng: number };
  brands: string[];
  description?: string;
  website?: string;
  featured?: boolean;
}

export interface CigarRecommendation {
  id: string;
  name: string;
  brand: string;
  vendorId?: string;
  imageUrl?: string;
  strength: 'mild' | 'medium' | 'medium-full' | 'full';
  flavors: string[];
  origin?: string;
  wrapper?: string;
  description?: string;
  price?: string;
}

export interface FestivalCheckIn {
  id: string;
  festivalId: string;
  timestamp: string;
  note?: string;
}

export interface FestivalNote {
  id: string;
  festivalId: string;
  vendorId?: string;
  vendorName?: string;
  cigarTried?: string;
  content: string;
  timestamp: string;
}

export interface FestivalBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  requirement: {
    type: 'check_in_count' | 'specific_festival' | 'vendors_visited';
    count?: number;
    festivalId?: string;
  };
}

export const FESTIVAL_BADGES: FestivalBadge[] = [
  {
    id: 'first-festival',
    name: 'Festival Rookie',
    description: 'Check in to your first cigar festival',
    icon: '🎟️',
    requirement: { type: 'check_in_count', count: 1 },
  },
  {
    id: 'festival-regular',
    name: 'Festival Regular',
    description: 'Check in to 3 cigar festivals',
    icon: '🎪',
    requirement: { type: 'check_in_count', count: 3 },
  },
  {
    id: 'festival-veteran',
    name: 'Festival Veteran',
    description: 'Check in to 5 cigar festivals',
    icon: '🏆',
    requirement: { type: 'check_in_count', count: 5 },
  },
  {
    id: 'ybor-attendee',
    name: 'Ybor City Aficionado',
    description: 'Attend the Ybor City Cigar Festival',
    icon: '🌴',
    requirement: { type: 'specific_festival', festivalId: 'ybor-cigar-fest' },
  },
  {
    id: 'pca-attendee',
    name: 'Industry Insider',
    description: 'Attend the PCA Trade Show',
    icon: '💼',
    requirement: { type: 'specific_festival', festivalId: 'pca-trade-show' },
  },
  {
    id: 'rmcf-attendee',
    name: 'Rocky Mountain High',
    description: 'Attend the Rocky Mountain Cigar Festival',
    icon: '🏔️',
    requirement: { type: 'specific_festival', festivalId: 'rmcf' },
  },
];

// Festival definitions - ordered chronologically (soonest first)
export const FESTIVALS: Festival[] = [
  {
    id: 'pca-trade-show',
    name: 'PCA Trade Show',
    shortName: 'PCA',
    location: 'New Orleans, LA',
    venue: 'Ernest N. Morial Convention Center',
    address: '900 Convention Center Blvd, New Orleans, LA 70130',
    description: 'The Premium Cigar Association\'s annual trade show - the industry\'s largest gathering of cigar manufacturers, retailers, and enthusiasts. Over 240 exhibiting companies and nearly 6,000 attendees.',
    date: 'April 17-20, 2026',
    time: 'Trade show hours vary by day',
    admissionInfo: 'Trade only • Registration required • Retailer/Industry credentials',
    website: 'https://pcashow.org/',
    highlights: [
      '240+ exhibiting cigar companies',
      'New product launches & exclusives',
      'Industry networking events',
      'Educational seminars & workshops',
      'Largest cigar trade event in the world',
    ],
    mapCenter: { lat: 29.9429, lng: -90.0638 },
    mapZoom: 15,
    active: true,
  },
  {
    id: 'great-lakes-cigar-fest',
    name: 'Great Lakes Cigar Fest',
    shortName: 'Great Lakes',
    location: 'Pontiac, MI',
    venue: 'M1 Concourse',
    address: 'M1 Concourse, Pontiac, MI',
    description: 'Michigan\'s premier cigar event powered in part by Drew Estate. Features spirit tastings, premium cigar selections, and VIP access to renowned cigar makers in a unique automotive venue.',
    date: 'June 27, 2026',
    time: 'TBA',
    admissionInfo: '21+ only • Ticket purchase required',
    website: 'https://greatlakescigarfest.com/',
    highlights: [
      'Powered by Drew Estate',
      'Premium spirit tastings',
      'VIP access to cigar makers',
      'Unique M1 Concourse venue',
      'Liga Privada & Acid cigars featured',
    ],
    mapCenter: { lat: 42.6389, lng: -83.2910 },
    mapZoom: 14,
    active: true,
  },
  {
    id: 'rocky-mountain-cigar-fest',
    name: 'Rocky Mountain Cigar Festival',
    shortName: 'RMCF',
    location: 'Broomfield, CO',
    venue: 'Omni Interlocken Resort',
    address: '500 Interlocken Blvd, Broomfield, CO 80021',
    description: 'Colorado\'s premier cigar event in the heart of the Rocky Mountain Front Range. A luxury experience featuring premium cigars, craft brews, fine spirits, and world-class cuisine.',
    date: 'August 22, 2026',
    time: '12pm - 6pm (VIP: 11am)',
    admissionInfo: '21+ only • Ticket purchase required • Rain or shine',
    website: 'https://rmcigarfestival.com/',
    highlights: [
      'Festival bag with 30+ premium cigars',
      'Meet cigar manufacturers & celebrities',
      'Free meal included with ticket',
      'Craft brews, wine & spirits',
      'VIP early access at 11am',
    ],
    mapCenter: { lat: 39.9283, lng: -105.1253 },
    mapZoom: 15,
    active: true,
  },
  {
    id: 'big-smoke-las-vegas',
    name: 'Big Smoke Las Vegas',
    shortName: 'Big Smoke',
    location: 'Las Vegas, NV',
    venue: 'Las Vegas Convention Center',
    address: 'Las Vegas, NV',
    description: 'Cigar Aficionado\'s premier two-evening event featuring 25+ handmade cigars, premium whisky and spirit samplings, gourmet food, and VIP experiences with top cigar makers.',
    date: 'October 31 - November 1, 2026',
    time: 'Evening event',
    admissionInfo: '21+ only • Ticket purchase required • VIP upgrades available',
    website: 'https://www.cigaraficionado.com/',
    highlights: [
      '25+ premium handmade cigars included',
      'Whisky & premium spirit samplings',
      'Gourmet food stations',
      'Meet legendary cigar makers',
      'VIP early access & exclusive lounges',
    ],
    mapCenter: { lat: 36.1699, lng: -115.1398 },
    mapZoom: 14,
    active: true,
  },
  {
    id: 'ybor-cigar-fest',
    name: 'Ybor City Cigar Festival',
    shortName: 'Ybor',
    location: 'Tampa, FL',
    venue: 'Centennial Park, Ybor City',
    address: '1800 E 8th Ave, Tampa, FL 33605',
    description: 'The world\'s largest cigar event, celebrating Tampa\'s rich cigar heritage since 1994. Featuring 150+ vendors, cigar rolling demonstrations, food trucks, and premium handmade cigars from around the world.',
    date: 'December 6, 2026',
    time: '11am - 5pm',
    admissionInfo: 'Free admission • Family friendly • Pet friendly',
    website: 'https://www.yborcigarfestival.com/',
    highlights: [
      'World\'s largest cigar event with 10,000+ attendees',
      '150+ vendors from around the world',
      'Live cigar rolling demonstrations',
      'Food truck alley & local brews',
      'Presented by Arturo Fuente & J.C. Newman',
    ],
    mapCenter: { lat: 27.9606, lng: -82.4374 },
    mapZoom: 16,
    active: true,
  },
];

// Sample vendors for all festivals
export const SAMPLE_VENDORS: Vendor[] = [
  // === YBOR CITY CIGAR FESTIVAL ===
  {
    id: 'arturo-fuente-ybor',
    name: 'Arturo Fuente',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'A1',
    brands: ['Arturo Fuente', 'Fuente Fuente OpusX', 'Don Carlos', 'Hemingway'],
    description: 'Presenting sponsor. Premium Dominican cigars since 1912, known for exceptional craftsmanship.',
    website: 'https://www.arturofuente.com/',
    featured: true,
  },
  {
    id: 'jc-newman-ybor',
    name: 'J.C. Newman Cigar Company',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'A2',
    brands: ['Brick House', 'Diamond Crown', 'El Baton', 'Perla del Mar'],
    description: 'Presenting sponsor. Four generations of cigar making, home of El Reloj factory in Ybor City.',
    website: 'https://www.jcnewman.com/',
    featured: true,
  },
  {
    id: 'padron-cigars-ybor',
    name: 'Padrón Cigars',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'B5',
    brands: ['Padrón', 'Padrón 1964 Anniversary', 'Padrón Family Reserve', 'Padrón 1926'],
    description: 'Nicaraguan excellence since 1964, consistently rated among the world\'s best.',
    website: 'https://www.padron.com/',
    featured: true,
  },
  {
    id: 'oliva-cigar-ybor',
    name: 'Oliva Cigar Co.',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'C3',
    brands: ['Oliva Serie V', 'Oliva Serie O', 'Oliva Connecticut Reserve', 'Oliva Serie V Melanio'],
    description: 'Family-owned Nicaraguan cigars with a legacy spanning five generations.',
    website: 'https://www.olivacigar.com/',
  },
  {
    id: 'my-father-cigars-ybor',
    name: 'My Father Cigars',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'A6',
    brands: ['My Father', 'Flor de las Antillas', 'Le Bijou 1922', 'La Opulencia'],
    description: 'García family craft from Nicaragua, multiple Cigar of the Year awards.',
    website: 'https://www.myfathercigars.com/',
    featured: true,
  },
  {
    id: 'drew-estate-ybor',
    name: 'Drew Estate',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'D4',
    brands: ['Liga Privada', 'Undercrown', 'Acid', 'Tabak Especial'],
    description: 'Innovative blends from Estelí, known for unique infused and traditional cigars.',
    website: 'https://www.drewestate.com/',
  },
  {
    id: 'rocky-patel-ybor',
    name: 'Rocky Patel',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'B7',
    brands: ['Rocky Patel Vintage', 'Decade', 'Sun Grown', 'Fifteenth Anniversary'],
    description: 'Award-winning premium cigars crafted in Honduras and Nicaragua.',
    website: 'https://www.rockypatel.com/',
  },
  {
    id: 'davidoff-ybor',
    name: 'Davidoff',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'C1',
    brands: ['Davidoff', 'Avo', 'Camacho', 'Zino'],
    description: 'Swiss luxury brand known for refined, elegant cigars.',
    website: 'https://www.davidoff.com/',
    featured: true,
  },
  {
    id: 'tampa-sweethearts-ybor',
    name: 'Tampa Sweethearts',
    festivalId: 'ybor-cigar-fest',
    boothNumber: 'D1',
    brands: ['Tampa Sweethearts', 'Have-a-Tampa'],
    description: 'Local Tampa brands celebrating the city\'s cigar heritage.',
  },

  // === PCA TRADE SHOW ===
  {
    id: 'arturo-fuente-pca',
    name: 'Arturo Fuente',
    festivalId: 'pca-trade-show',
    boothNumber: '100',
    brands: ['Arturo Fuente', 'Fuente Fuente OpusX', 'Don Carlos', 'Hemingway'],
    description: 'Premium Dominican cigars since 1912, showcasing new releases and limited editions.',
    website: 'https://www.arturofuente.com/',
    featured: true,
  },
  {
    id: 'padron-pca',
    name: 'Padrón Cigars',
    festivalId: 'pca-trade-show',
    boothNumber: '200',
    brands: ['Padrón', 'Padrón 1964 Anniversary', 'Padrón Family Reserve', 'Padrón 1926'],
    description: 'Nicaraguan excellence since 1964, new releases for retailers.',
    website: 'https://www.padron.com/',
    featured: true,
  },
  {
    id: 'perdomo-pca',
    name: 'Perdomo Cigars',
    festivalId: 'pca-trade-show',
    boothNumber: '150',
    brands: ['Perdomo Lot 23', 'Perdomo Reserve', 'Perdomo 20th Anniversary'],
    description: 'Nicaraguan cigars with a focus on estate-grown tobacco.',
    website: 'https://www.perdomocigars.com/',
  },
  {
    id: 'la-flor-dominicana-pca',
    name: 'La Flor Dominicana',
    festivalId: 'pca-trade-show',
    boothNumber: '300',
    brands: ['La Flor Dominicana', 'Ligero', 'Double Ligero', 'Air Bender'],
    description: 'Dominican powerhouse known for full-bodied, complex cigars.',
    website: 'https://www.laflordominicana.com/',
  },
  {
    id: 'general-cigar-pca',
    name: 'General Cigar',
    festivalId: 'pca-trade-show',
    boothNumber: '250',
    brands: ['Macanudo', 'Partagas', 'CAO', 'Cohiba', 'La Gloria Cubana'],
    description: 'Major manufacturer with iconic brands spanning all price points.',
    website: 'https://www.generalcigar.com/',
    featured: true,
  },
  {
    id: 'plasencia-pca',
    name: 'Plasencia Cigars',
    festivalId: 'pca-trade-show',
    boothNumber: '350',
    brands: ['Plasencia Alma Fuerte', 'Plasencia Reserva Original', 'Cosecha 146'],
    description: 'Five generations of tobacco expertise, premium boutique cigars.',
    website: 'https://www.plasenciacigars.com/',
    featured: true,
  },
  {
    id: 'aganorsa-leaf-pca',
    name: 'Aganorsa Leaf',
    festivalId: 'pca-trade-show',
    boothNumber: '400',
    brands: ['Aganorsa Leaf', 'Casa Fernandez', 'JFR', 'Illusione'],
    description: 'Nicaraguan grower-manufacturer with highly sought-after leaf.',
    website: 'https://www.aganorsaleaf.com/',
  },
  {
    id: 'espinosa-pca',
    name: 'Espinosa Cigars',
    festivalId: 'pca-trade-show',
    boothNumber: '420',
    brands: ['Espinosa', 'Laranja', 'La Bomba', '601'],
    description: 'Boutique brand known for bold, flavorful blends.',
    website: 'https://www.espinosacigars.com/',
  },

  // === ROCKY MOUNTAIN CIGAR FESTIVAL ===
  {
    id: 'rocky-patel-rmcf',
    name: 'Rocky Patel Premium Cigars',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Rocky Patel Vintage', 'Decade', 'Sun Grown', 'Number 6'],
    description: 'Award-winning premium cigars, a festival favorite.',
    website: 'https://www.rockypatel.com/',
    featured: true,
  },
  {
    id: 'drew-estate-rmcf',
    name: 'Drew Estate',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Liga Privada', 'Undercrown', 'Acid', 'Herrera Estelí'],
    description: 'Innovative blends from Estelí, Nicaragua.',
    website: 'https://www.drewestate.com/',
    featured: true,
  },
  {
    id: 'la-aroma-de-cuba-rmcf',
    name: 'Ashton/La Aroma de Cuba',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Ashton', 'La Aroma de Cuba', 'San Cristobal'],
    description: 'Premium brands distributed by Ashton Cigars.',
    website: 'https://www.ashtoncigars.com/',
  },
  {
    id: 'tatuaje-rmcf',
    name: 'Tatuaje Cigars',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Tatuaje', 'Tatuaje Monsters', 'El Triunfador'],
    description: 'Boutique Cuban-style cigars from Miami.',
    website: 'https://www.tatuajecigars.com/',
    featured: true,
  },
  {
    id: 'foundation-cigar-rmcf',
    name: 'Foundation Cigar Company',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['The Tabernacle', 'Charter Oak', 'El Güegüense'],
    description: 'Boutique brand founded by industry veteran Nick Melillo.',
    website: 'https://www.foundationcigar.com/',
  },
  {
    id: 'room101-rmcf',
    name: 'Room101 Cigars',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Room101', 'Farce', 'The Big Payback'],
    description: 'Edgy, artistic brand with unique blends.',
    website: 'https://www.room101cigars.com/',
  },
  {
    id: 'alec-bradley-rmcf',
    name: 'Alec Bradley',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Alec Bradley Prensado', 'Black Market', 'Project 40'],
    description: 'Premium cigars from Honduras, multiple Cigar of the Year awards.',
    website: 'https://www.alecbradley.com/',
  },
  {
    id: 'crowned-heads-rmcf',
    name: 'Crowned Heads',
    festivalId: 'rocky-mountain-cigar-fest',
    brands: ['Four Kicks', 'Jericho Hill', 'Le Carême'],
    description: 'Boutique brand with roots in Nashville, unique blends.',
    website: 'https://www.crownedheads.com/',
  },
];

// Sample cigar recommendations
export const SAMPLE_RECOMMENDATIONS: CigarRecommendation[] = [
  {
    id: 'fuente-hemingway',
    name: 'Hemingway',
    brand: 'Arturo Fuente',
    vendorId: 'arturo-fuente',
    strength: 'medium',
    flavors: ['cedar', 'spice', 'creamy'],
    origin: 'Dominican Republic',
    wrapper: 'Cameroon',
    description: 'A classic, perfectly constructed cigar known for its smooth draw and rich, complex flavors.',
  },
  {
    id: 'padron-1964',
    name: '1964 Anniversary',
    brand: 'Padrón',
    vendorId: 'padron-cigars',
    strength: 'full',
    flavors: ['cocoa', 'coffee', 'spice'],
    origin: 'Nicaragua',
    wrapper: 'Maduro',
    description: 'A box-pressed classic with hints of cocoa, coffee, and a hint of spice. Consistently rated among the best.',
  },
  {
    id: 'montecristo-no2',
    name: 'No. 2',
    brand: 'Montecristo',
    strength: 'medium',
    flavors: ['cedar', 'leather', 'sweet'],
    origin: 'Cuba',
    wrapper: 'Habano',
    description: 'The quintessential torpedo, delivering creamy notes of cedar, leather, and a sweet finish.',
  },
  {
    id: 'rocky-patel-decade',
    name: 'Decade',
    brand: 'Rocky Patel',
    vendorId: 'rocky-patel',
    strength: 'full',
    flavors: ['cherry', 'nuts', 'pepper'],
    origin: 'Honduras',
    wrapper: 'Sumatra',
    description: 'A highly acclaimed, smooth yet complex smoke with notes of dark cherry, nuts, and black pepper.',
  },
];
